import { useEffect, useMemo, useState, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Spinner } from "elements";
import styled from "styled-components";
import Loading from "components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { getCookie } from "utils/StorageVariables";
import { IsUser } from "Redux/User/action";
import { RetrieveMasterPlanDetails, ResetMasterPlanDetails } from "Redux/RetrieveMasterPlan/action";
import { SetSelectedGoalAction, clearTemporarilyVisibleCompletedGoals } from "Redux/Global/action";
import SubHeader from "components/Scheduler/SubHeader";
import Instructions from 'components/Instructions';
import StepsHeader from "../StepsHeader";
import AreasSection from "../AreasSection";
import sidebarCloseIcon from "assets/images/journeys/sidebar-close.svg";
import { Drawer } from "antd";

import {
  ACTIONS_PATH,
  GOALS_PATH,
  JOURNEY_PATH,
  PREMISE_PATH,
  MASTER_PATH,
  MEASUREMENT_PATH,
  MISSION_PATH,
  PRIORITIZE_PATH,
  PURPOSE_PATH,
} from "utils/routes";

import GoalsSection from "../GoalsSection";
import Sidebar from "../Sidebar";
import useDeviceType from "../../../Hooks/ResponsiveHook";

import { items } from "./data";

const SortArr = (data) => {
  if (!data) return [];

  return data?.sort(function (a, b) {
    return a?.goal?.priority - b?.goal?.priority;
  });
};

const JourneysLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const goal = queryParams.get('g');

  const { userDetail } = useSelector((state) => state.userDetailReducer);
  const { data, loading } = useSelector((state) => state.listAreaReducer);
  const { masterPlanDetails } = useSelector(
    (state) => state.RetrieveMasterPlanDetailsReducer
  );
  const { selectedGoalAction, temporarilyVisibleCompletedGoals } = useSelector((state) => state.globalReducer);

  const screenSizes = useDeviceType();
  const isMobile = screenSizes === "mobile";
  const isTablet = screenSizes === "iPad";
  const [visible, setVisible] = useState(false);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  useEffect(() => {
    let MasterPlanId = getCookie("MasterPlanId");
    if (!MasterPlanId) return;
    dispatch(RetrieveMasterPlanDetails(MasterPlanId));
    
    return () => {
      dispatch(ResetMasterPlanDetails());
    }
  }, []);

  useEffect(() => {
    if (userDetail === null) {
      let userData = JSON.parse(getCookie("userData"));
      dispatch(IsUser(userData));
    }
  }, [userDetail]);

  // Goal selection logic moved from GoalsSection
  const sortedData = SortArr(masterPlanDetails?.areas?.filter(area =>
    !area.goal?.completed || temporarilyVisibleCompletedGoals.has(area.goal?.id)
  ));

  useEffect(() => {
    if (!sortedData)
      return;

    let selectedGoal = sortedData.find((item) => item?.goal?.id === goal);

    if (selectedGoal) {
      dispatch(
        SetSelectedGoalAction({
          ...selectedGoal,
          i: sortedData.indexOf(selectedGoal),
        })
      );
    }
  }, [sortedData, goal]);

  // Track previous pathname to detect navigation from prioritize
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (!masterPlanDetails) return;

    if (location.pathname === JOURNEY_PATH) {
      dispatch(ResetMasterPlanDetails());
      return;
    }

    // Only run this logic on location.pathname change
    if (sortedData && sortedData.length > 0) {
      const prevPathname = prevPathnameRef.current;
      const currentPathname = location.pathname;
      
      // Check if we're coming from PRIORITIZE_PATH to any other path
      const comingFromPrioritize = prevPathname === PRIORITIZE_PATH && currentPathname !== PRIORITIZE_PATH;
      
      // Check if we're navigating between PURPOSE_PATH and ACTIONS_PATH (should preserve selection)
      const navigatingBetweenPurposeAndActions =
        (prevPathname === PURPOSE_PATH && currentPathname === ACTIONS_PATH) ||
        (prevPathname === ACTIONS_PATH && currentPathname === PURPOSE_PATH);

      if (comingFromPrioritize) {
        // Always select the first goal when coming from prioritize
        dispatch(
          SetSelectedGoalAction({
            ...sortedData[0],
            i: 0,
          })
        );
      } else if (!navigatingBetweenPurposeAndActions) {
        // For other navigation (not between PURPOSE and ACTIONS), apply existing logic
        
        // Check if current selectedGoalAction is still present in sortedData
        const stillExists = sortedData.some(
          (item) => item?.goal?.id === selectedGoalAction?.goal?.id
        );

        if (!stillExists) {
          // If not, select the first item in sortedData
          dispatch(
            SetSelectedGoalAction({
              ...sortedData[0],
              i: 0,
            })
          );
        } else if (selectedGoalAction && selectedGoalAction?.i !== -1) {
          // If still exists, keep the current selection (refresh from sortedData)
          const idx = sortedData.findIndex(
            (item) => item?.goal?.id === selectedGoalAction?.goal?.id
          );

          dispatch(
            SetSelectedGoalAction({
              ...sortedData[idx],
              i: idx,
            })
          );
        }
      }

      dispatch(clearTemporarilyVisibleCompletedGoals());
    }

    // Update the previous pathname ref
    prevPathnameRef.current = location.pathname;
  }, [masterPlanDetails?.id, sortedData.length, location.pathname]);

  const step = useMemo(() => {
    switch (location.pathname) {
      case MISSION_PATH:
        return 1;
      case PRIORITIZE_PATH:
        return 2;
      case GOALS_PATH:
        return 3;
      case MEASUREMENT_PATH:
        return 4;
      case PURPOSE_PATH:
        return 5;
      case ACTIONS_PATH:
        return 6;
      case MASTER_PATH:
        return 10;
    }

    return 0;
  }, [location.pathname]);

  return (
    <Wrapper>
      <Loading />

      {location.pathname !== JOURNEY_PATH &&
        location.pathname !== PREMISE_PATH && (
          <SubHeader title="Goal Setting Process">
            <StepsHeader items={items} step={step} isMasterPlanActive={masterPlanDetails?.viewed_masterplan_screen} withLinks withLabels />
          </SubHeader>
        )}

      <PagesWrapper>
        {!isMobile && !isTablet && (
          <>
            {step === 1 || step === 2 || step === 10 ? (
              <Sidebar title="Area Descriptions" secondary>
                {loading
                  ? <SpinnerWrapper>
                      <Spinner variant="secondary" />
                    </SpinnerWrapper>
                  : <AreasSection areas={data} />
                }
              </Sidebar>
            ) : step === 0 ? null : (
              <GoalsSection step={step} />
            )}
          </>
        )}
        {(isMobile || isTablet) &&
          location.pathname !== JOURNEY_PATH &&
          location.pathname !== PREMISE_PATH && (
            <DrawerContainer onClick={showDrawer}>
              <img
                src={sidebarCloseIcon}
                alt=""
              />
            </DrawerContainer>
          )}
        <PageLayout>
          <Outlet />
        </PageLayout>
      </PagesWrapper>
      {(isMobile || isTablet) &&
        location.pathname !== JOURNEY_PATH &&
        location.pathname !== PREMISE_PATH && (
          <Drawer
            width={isTablet ? "50%" : "100%"}
            placement="left"
            onClose={onClose}
            open={visible}
            headerStyle={{ padding: 0 }}
            closable={false}
            getContainer={false}
            style={{ position: 'absolute', top: '60px' }}
            bodyStyle={{ padding: 0 }}
          >
           {step === 1 || step === 2 || step === 10 ? (
              <Sidebar active={0} CustomWidth={100} title="Area Descriptions" secondary onToggle={onClose}>
                {loading
                  ? <SpinnerWrapper>
                      <Spinner variant="secondary" />
                    </SpinnerWrapper>
                  : <AreasSection areas={data} />
                }
              </Sidebar>
            ) : step === 0 ? null : (
              <GoalsSection active={0} onClose={onClose} CustomWidth={100} step={step} />
            )}
          </Drawer>
        )}

      <Instructions step={step} />
    </Wrapper>
  );
};

export default JourneysLayout;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: calc(100dvh - 60px);
`;

const PagesWrapper = styled.div`
  display: flex;
  flex: 1;
  height: calc(100vh - 106px);
  width: 100%;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: 1439px) {
    height: calc(100vh - 118px);
  }

  ${({ theme }) => theme.max("sm")`
    height: calc(100dvh - 60px);
  }`}
`;

const PageLayout = styled.div`
  flex: 1;
  height: calc(100% - 60px - 50px);

  ${({ theme }) => theme.max("sm")`
    height: 100%;
    overflow: hidden;
  }`}
`;

const DrawerContainer = styled.div`
  height: 40px,
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 3;
  left: 8px,
  top: 10px;

  @media screen and (max-width: 743px) {
    height: 20px;
    top: 10px;
    left: 7px;
  }

  @media screen and (max-width: 365px) {
    top: 20px;
  }

  @media (min-width: 744px) and (max-width: 1256px) {
    top: 10px;
    left: 7px;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
